/**
 * addressCorroboration — the reactive "corroborated yet?" rule for the
 * Address Verification capture (03-DETERMINISTIC-TILES-DECISION §3 / §3.1,
 * Bill 2026-06-11 relocation into the Address & Contact section).
 *
 * Single predicate: the Address Verification tile appears when, and only
 * when, the applicant's STATED physical address is NOT already corroborated
 * by a captured document. Concretely:
 *
 *   - Passport only (no address on the doc)      → not corroborated → tile shown
 *   - DL captured, address MATCHES stated         → corroborated — the DL IS the
 *                                                   proof → tile suppressed
 *   - DL captured, address DIFFERS from stated    → ID-address conflict → tile
 *                                                   shown (REQUIRED when the
 *                                                   admin mismatch gate is on)
 *   - No stated address yet (path collects docs   → not corroborated → tile
 *     first)                                        shown (§3 design point 1)
 *
 * REACTIVE (§3.1): this is a derived/computed state — the consumer calls it
 * on every render, so it re-evaluates on (a) each capture (the extract lands
 * in `person.extra.extracts`) and (b) every FSR address-field edit.
 *
 * Comparison rules ported from the Stage-4 `detectAddressMismatch` (now
 * retired with the grid's OPTIONAL→REQUIRED flip): case/whitespace-insensitive
 * component compare, ZIP trimmed to 5 (USPS+DPV upstream aligns formats).
 * Street match is the minimum bar for corroboration — a doc that only shares
 * a ZIP code does not corroborate an address.
 */

import type { RaoPerson } from '../../../types';
import { docTypeBranding } from './captureRouting';
import { joinStreetLines, normalizeStreet } from '../shared/streetLine';

export interface AddressCorroboration {
  /** True when ≥1 captured doc's extracted address matches the stated address. */
  readonly corroborated: boolean;
  /** Friendly label of the doc that corroborates (e.g. "Driver's License"). */
  readonly corroboratedBy?: string;
  /**
   * Set when a captured ID carries an address that CONFLICTS with the stated
   * address (the escalation trigger for the admin
   * `cip.addressVerification.requireOnMismatch` gate).
   */
  readonly idAddressConflict?: { docLabel: string; detail: string };
  /** True when the FSR hasn't entered any stated address yet (no diff possible). */
  readonly noStatedAddress: boolean;
}

interface ExtractFieldShape {
  value?: unknown;
}

function norm(s: string | undefined | null): string {
  return (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function fieldStr(fields: Record<string, ExtractFieldShape>, key: string): string {
  const v = fields[key]?.value;
  return typeof v === 'string' ? norm(v) : '';
}

/** Street + city as one run of letters and digits (normalized street, so "Road" = "Rd"). */
function compact(street: string, city: string): string {
  return `${street}${city}`.replace(/[^a-z0-9]/g, '');
}

function labelForExtractKey(docKey: string): string {
  return docTypeBranding(docKey)?.label ?? docKey;
}

/**
 * Derive the corroboration state from the live draft. Pure — call per render.
 */
export function deriveAddressCorroboration(person: RaoPerson): AddressCorroboration {
  // Street compared as a delivery line: "Lane" = "Ln", "Apartment C" = "Apt C" (Bill 2026-10-06).
  const statedAddr = normalizeStreet(joinStreetLines(person.addr, person.addr2));
  const statedCity = norm(person.city);
  const statedState = norm(person.state);
  const statedZip5 = norm(person.zip).slice(0, 5);
  const noStatedAddress = !statedAddr && !statedCity && !statedState && !statedZip5;

  const extractsRoot = ((person.extra as Record<string, unknown> | undefined)?.extracts ??
    {}) as Record<string, unknown>;

  let conflict: { docLabel: string; detail: string } | undefined;

  for (const [docKey, raw] of Object.entries(extractsRoot)) {
    if (!raw || typeof raw !== 'object') continue;
    const fields = raw as Record<string, ExtractFieldShape>;
    const exAddr = normalizeStreet(joinStreetLines(fieldStr(fields, 'addr'), fieldStr(fields, 'addr2') || fieldStr(fields, 'addressLine2')));
    const exCity = fieldStr(fields, 'city');
    const exState = fieldStr(fields, 'state');
    const exZip5 = fieldStr(fields, 'zip').slice(0, 5);
    // Doc carries no address (passport / visa / SSN card) — can't corroborate.
    if (!exAddr && !exCity && !exState && !exZip5) continue;
    if (noStatedAddress) continue; // nothing to diff against yet

    // CHG-0083 (MacKenzie Mott 2026-10-21): the ID scan can split a word across the street and city lines
    // ("… Rd" / "Rolla" read as "…" / "Rdrolla"). The officer corrects the form, but the scan's reading stays,
    // so street and city "differed" and address proof was required for the same address. When street + city
    // read as one run of letters are identical, they are the same address split differently.
    const streetCityMatch = !!exAddr && !!exCity && !!statedAddr && !!statedCity
      && compact(exAddr, exCity) === compact(statedAddr, statedCity);

    const diverging =
      (!streetCityMatch && exAddr && statedAddr && exAddr !== statedAddr) ? 'street differs'
      : (!streetCityMatch && exCity && statedCity && exCity !== statedCity) ? 'city differs'
      : (exState && statedState && exState !== statedState) ? 'state differs'
      : (exZip5 && statedZip5 && exZip5 !== statedZip5) ? 'ZIP differs'
      : null;

    if (diverging) {
      if (!conflict) conflict = { docLabel: labelForExtractKey(docKey), detail: diverging };
      continue;
    }
    // No diverging component AND the doc's street matches the stated street —
    // this doc IS the proof.
    if (streetCityMatch || (exAddr && statedAddr && exAddr === statedAddr)) {
      return {
        corroborated: true,
        corroboratedBy: labelForExtractKey(docKey),
        noStatedAddress: false,
      };
    }
  }

  return {
    corroborated: false,
    ...(conflict ? { idAddressConflict: conflict } : {}),
    noStatedAddress,
  };
}
